/**
 * VRG-471 — "Did you mean …?" for a mistyped recipient domain.
 *
 * ⚠️ THIS SUGGESTS. IT NEVER BLOCKS. Every function here is advisory and the
 * caller MUST leave the typed address submittable. An unusual-but-real domain
 * (a self-hosted vanity domain, a fresh company domain, a regional provider we
 * have never seen) is exactly the input a heuristic like this gets wrong, and the
 * cost of being wrong in the blocking direction is a transfer that cannot be
 * made at all. The only blocking check in this module's sibling
 * `validate-email-address.ts` is the TLD lookup, which is a closed set of facts
 * rather than a guess.
 *
 * WHY IT MATTERS EVEN THOUGH TLD VALIDATION EXISTS. TLD validation catches
 * `usevergence.cin` because `.cin` is not delegated. It does nothing for
 * `usevergance.com`, which is a perfectly real-looking domain that happens not to
 * be the one the sender meant. Suggestion is the cheap mitigation for that
 * near-miss class; the bounce webhook is the durable fix for the rest.
 *
 * ALGORITHM. Damerau-Levenshtein (optimal string alignment, so an adjacent
 * transposition — `gmial`/`gmail` — costs 1 rather than 2), run twice:
 *
 *   1. against WHOLE known domains, catching `gmial.com`, `hotnail.com`, and any
 *      domain the caller passes in (see `knownDomains` below — this is how the
 *      sender's own domain and the company's existing recipients get considered);
 *   2. if that finds nothing AND the TLD is not a real one, against a short list
 *      of popular TLDs, keeping the second-level part untouched.
 *
 * Pass 2 is why `usevergence.cin` -> `usevergence.com` works with no caller
 * context at all. It also explains the length penalty in `bestTldMatch`: plain
 * edit distance makes `.cn` (distance 1, a deletion) beat `.com` (distance 2, two
 * substitutions), which is the wrong answer. Typos overwhelmingly preserve
 * length, so a candidate of a different length pays for the difference, and ties
 * break toward the more popular TLD.
 */

import {isKnownTld, splitEmailAddress, tldOf} from './validate-email-address.js';

/**
 * Consumer mailbox providers, which is where the overwhelming majority of
 * recipient typos land. NOT an allow-list and NOT exhaustive — purely the set of
 * strings we are willing to propose a correction toward.
 */
export const POPULAR_EMAIL_DOMAINS: readonly string[] = [
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'hotmail.com',
  'hotmail.co.uk',
  'outlook.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'comcast.net',
  'verizon.net',
  'att.net',
  'sbcglobal.net',
  'btinternet.com',
  'sky.com',
  'gmx.com',
  'gmx.de',
  'web.de',
  'mail.com',
  'zoho.com',
  'fastmail.com',
  'yandex.com',
  'qq.com',
  '163.com',
];

/**
 * Ordered by how likely a mistyped ending was meant to be this one — the order IS
 * the tie-break, so it is not alphabetical and should not be "tidied".
 */
export const POPULAR_TLDS: readonly string[] = [
  'com',
  'net',
  'org',
  'co',
  'io',
  'edu',
  'gov',
  'uk',
  'de',
  'fr',
  'nl',
  'es',
  'it',
  'se',
  'ch',
  'ca',
  'au',
  'nz',
  'ie',
  'jp',
  'cn',
  'in',
  'br',
  'mx',
  'me',
  'inc',
  'app',
  'dev',
  'ai',
  'info',
  'biz',
  'us',
  'eu',
];

export interface EmailSuggestion {
  /** The corrected domain, e.g. `usevergence.com`. */
  domain: string;
  /** The whole corrected address, ready to drop into the input. */
  full: string;
}

export interface SuggestEmailCorrectionOptions {
  /**
   * Domains to consider IN ADDITION to `POPULAR_EMAIL_DOMAINS`. Pass whatever
   * context the calling surface has: the signed-in user's own domain, the
   * company's domain, the domains of owners this company has transferred to
   * before. `usevergence.com` is not a public mailbox provider, so without
   * context like this it is only reachable via the TLD pass.
   */
  knownDomains?: readonly string[];
}

/**
 * Optimal string alignment distance. Not the unrestricted Damerau edit distance
 * (which allows editing between transposed characters); the restricted variant is
 * the standard choice for typo correction and is O(n·m) with no extra bookkeeping.
 */
export function damerauLevenshtein(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  if (!a.length) {
    return b.length;
  }
  if (!b.length) {
    return a.length;
  }

  const rows: number[][] = [];
  for (let i = 0; i <= a.length; i += 1) {
    rows.push(new Array<number>(b.length + 1).fill(0));
    rows[i][0] = i;
  }
  for (let j = 0; j <= b.length; j += 1) {
    rows[0][j] = j;
  }

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;

      rows[i][j] = Math.min(
        rows[i - 1][j] + 1,
        rows[i][j - 1] + 1,
        rows[i - 1][j - 1] + cost,
      );

      if (
        i > 1 &&
        j > 1 &&
        a[i - 1] === b[j - 2] &&
        a[i - 2] === b[j - 1]
      ) {
        rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + cost);
      }
    }
  }

  return rows[a.length][b.length];
}

/**
 * How far a domain may be from a candidate before the "correction" is really a
 * different domain. Scaled by length because 2 edits in `bt.com` is a rewrite
 * while 2 edits in `usevergence.com` is a slip.
 */
function thresholdFor(domain: string): number {
  if (domain.length <= 5) {
    return 1;
  }
  if (domain.length <= 10) {
    return 2;
  }
  return 3;
}

function bestDomainMatch(
  domain: string,
  candidates: readonly string[],
): string | null {
  const limit = thresholdFor(domain);
  let best: string | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const candidate of candidates) {
    if (candidate === domain) {
      return null;
    }

    const distance = damerauLevenshtein(domain, candidate);

    if (distance <= limit && distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }

  return best;
}

function bestTldMatch(tld: string): string | null {
  let best: string | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const candidate of POPULAR_TLDS) {
    if (candidate === tld) {
      return null;
    }

    // The length penalty is load-bearing — see the module docblock. Without it a
    // shorter TLD that is one deletion away always beats the same-length one the
    // user actually meant.
    const score =
      damerauLevenshtein(tld, candidate) +
      Math.abs(tld.length - candidate.length);

    // Strictly-less keeps the first (most popular) candidate on a tie.
    if (score < bestScore) {
      best = candidate;
      bestScore = score;
    }
  }

  return bestScore <= 2 ? best : null;
}

export function suggestEmailCorrection(
  raw: string,
  options: SuggestEmailCorrectionOptions = {},
): EmailSuggestion | null {
  const parts = splitEmailAddress(raw);

  if (!parts) {
    return null;
  }

  const {local, domain} = parts;

  const candidates = [
    ...POPULAR_EMAIL_DOMAINS,
    ...(options.knownDomains ?? []).map(entry => entry.trim().toLowerCase()),
  ].filter(Boolean);

  // Pass 1 — the whole domain against domains we know are real.
  const domainMatch = bestDomainMatch(domain, candidates);

  if (domainMatch) {
    return {domain: domainMatch, full: `${local}@${domainMatch}`};
  }

  // Pass 2 — only when the ending is not a real TLD. A real-but-unexpected
  // ending (`.co`, `.dev`, `.ist`) is left completely alone: proposing `.com` to
  // someone who correctly typed `.co` is noise, and noise is how people learn to
  // ignore the suggestion that mattered.
  const tld = tldOf(domain);

  if (isKnownTld(tld)) {
    return null;
  }

  const tldMatch = bestTldMatch(tld);

  if (!tldMatch) {
    return null;
  }

  const corrected = `${domain.slice(0, domain.length - tld.length)}${tldMatch}`;

  if (corrected === domain) {
    return null;
  }

  return {domain: corrected, full: `${local}@${corrected}`};
}
